// Copyright (c) Docugami, Inc. All rights reserved.
import { Logger, LoggerCategory } from "utils/Logger";
import { StringBuilder } from "utils/StringBuilder";
import { ComparisonUnitToken } from "./ComparisonUnitToken";
import { CorrelatedSequence } from "./CorrelatedSequence";
import { CorrelationStatus } from "./CorrelationStatus";
import { LcsSettings } from "./LcsSettings";
import { IDgWordCanvasProxyCallbacks } from "./worker/worker";

const Enumerable = require("../../../../../utils/linq");

export function lcs(
    tokens1: ComparisonUnitToken[],
    tokens2: ComparisonUnitToken[],
    lcsSettings: LcsSettings,
    proxyCallbacks: IDgWordCanvasProxyCallbacks
): CorrelatedSequence[]
{
    proxyCallbacks.bucketTimer(4, "100-est/020-corr/200-Lcs/100-misc");

    const csList: CorrelatedSequence[] = [
        new CorrelatedSequence(
            tokens1,
            tokens2,
            CorrelationStatus.Unknown,
            "lcs-1"
        ),
    ];

    let lcsIteration = 0;
    while (true) {
        if (++lcsIteration % 50 === 0) {
            Logger.LogTrace(LoggerCategory.AssistedAuthoring, lcsIteration);
        }

        if (LcsSettings.lcsDebugFalse) {
            const sb = new StringBuilder();
            for (let i = 0; i < csList.length; ++i)
            {
                sb.appendLine(csList[i].toString(false));
            }
            Logger.LogTrace(LoggerCategory.AssistedAuthoring, sb.toString());
        }

        let i = 0;
        for (i = 0; i < csList.length; ++i)
        {
            if (csList[i].correlationStatus === CorrelationStatus.Unknown) {
                break;
            }
        }
        if (i === csList.length) {
            return csList;
        }

        const unknown = csList[i];
        const unknownIndex = i;

        if (LcsSettings.lcsDebugFalse) {
            const str = csList[i].toString(false) + "\n";
            Logger.LogTrace(LoggerCategory.AssistedAuthoring, str);
        }

        proxyCallbacks.bucketTimer(4, "100-est/020-corr/200-Lcs/200-commonBegEnd");
        const commonBeginningEndSequence = findCommonAtBeginningAndEnd(
            unknown,
            lcsSettings
        );
        if (commonBeginningEndSequence !== null) {
            localSplice(csList, unknownIndex, commonBeginningEndSequence);
            continue;
        }

        proxyCallbacks.bucketTimer(4, "100-est/020-corr/200-Lcs/300-doLcsAlgorithm");
        const lcsSequence = doLcsAlgorithm(unknown, lcsSettings);
        if (lcsSequence !== null) {
            localSplice(csList, unknownIndex, lcsSequence);
            continue;
        }

        break;
    }

    return csList;
}

function doLcsAlgorithm(
    unknown: CorrelatedSequence,
    lcsSettings: LcsSettings
): CorrelatedSequence[] {
    const cul1 = unknown.comparisonUnitArray1 as ComparisonUnitToken[];
    const cul2 = unknown.comparisonUnitArray2 as ComparisonUnitToken[];

    // first thing to do - if we have an unknown with zero length on left or right side, create appropriate
    // this is a code optimization that enables easier processing of cases elsewhere.
    if (cul1.length > 0 && cul2.length === 0) {
        const newSequence: CorrelatedSequence[] = [
            new CorrelatedSequence(
                cul1,
                null,
                CorrelationStatus.Deleted,
                "doLcsAlgorithm-1"
            ),
        ];
        return newSequence;
    } else if (cul1.length === 0 && cul2.length > 0) {
        const newSequence: CorrelatedSequence[] = [
            new CorrelatedSequence(
                null,
                cul2,
                CorrelationStatus.Inserted,
                "doLcsAlgorithm-2"
            ),
        ];
        return newSequence;
    } else if (cul1.length === 0 && cul2.length === 0) {
        const emptySequence: CorrelatedSequence[] = [];
        return emptySequence; // this will effectively remove the unknown with no data on either side from the current data model.
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // We know for our use case that *very* often, a long sequence starts very near to the front of the cul1 and cul2.
    // Further, because of the issue where dgml contains list items, we know that the sequence will be closer to the beginning of cul1
    // than to the beginning of cul2.  But we know it is there.  A complicating factor is that in the space before the long sequence,
    // there are likely to be small two or three token matches. We should disregard those.  We only want to snag this long sequence as quickly
    // as possible.

    // so starting with cul1, for the first FastFindSearchLength tokens, look for a token list in cul2 with more than DgWordCanvas.FastFindSearchMatch tokens.
    let fastFound1 = -1;
    let fastFound2 = -1;
    let deepBreak = false;
    const searchUpTo = Math.min(
        Math.min(cul1.length, cul2.length),
        lcsSettings.fastFindSearchLength
    );
    for (let fastI1 = 0; fastI1 < searchUpTo; fastI1++) {
        const searchUpTo2 = Math.min(
            cul2.length,
            lcsSettings.fastFindSearchLength
        );
        for (let fastI2 = 0; fastI2 < searchUpTo2; fastI2++) {
            const seq1 = Enumerable.from(cul1)
                .skip(fastI1)
                .take(lcsSettings.fastFindSearchMatch);
            const seq2 = Enumerable.from(cul2)
                .skip(fastI2)
                .take(lcsSettings.fastFindSearchMatch);

            const countCommonSpecial = seq1
                .zip(seq2, (pu1: any, pu2: any) => {
                    return {
                        pu1: pu1,
                        pu2: pu2
                    };
                })
                .takeWhile(
                    (pair: any) =>
                        pair.pu1.tokenString === pair.pu2.tokenString
                )
                .count();

            if (countCommonSpecial === lcsSettings.fastFindSearchMatch) {
                fastFound1 = fastI1;
                fastFound2 = fastI2;
                deepBreak = true;
                break;
            }
        }
        if (deepBreak) {
            break;
        }
    }

    if (fastFound1 !== -1) {
        let thisSequenceLength = 0;
        const maxI1 = cul1.length;
        const maxI2 = cul2.length;
        let fi1 = fastFound1;
        let fi2 = fastFound2;
        while (true) {
            if (cul1[fi1].tokenString !== cul2[fi2].tokenString) {
                break;
            }
            ++thisSequenceLength;
            ++fi1;
            ++fi2;
            if (fi1 === maxI1) {
                break;
            }
            if (fi2 === maxI2) {
                break;
            }
        }

        if (thisSequenceLength > lcsSettings.detailThreshold) {
            const newSequence: CorrelatedSequence[] = [];

            let currentI1 = fastFound1;
            let currentI2 = fastFound2;
            if (currentI1 !== 0 || currentI2 !== 0) {
                const csUnknown: CorrelatedSequence = new CorrelatedSequence(
                    Enumerable.from(unknown.comparisonUnitArray1)
                        .take(currentI1)
                        .toArray(),
                    Enumerable.from(unknown.comparisonUnitArray2)
                        .take(currentI2)
                        .toArray(),
                    CorrelationStatus.Unknown,
                    "doLcsAlgorithm-3"
                );
                newSequence.push(csUnknown);
            }

            const csEqual: CorrelatedSequence = new CorrelatedSequence(
                Enumerable.from(unknown.comparisonUnitArray1)
                    .skip(currentI1)
                    .take(thisSequenceLength)
                    .toArray(),
                Enumerable.from(unknown.comparisonUnitArray2)
                    .skip(currentI2)
                    .take(thisSequenceLength)
                    .toArray(),
                CorrelationStatus.Equal,
                "doLcsAlgorithm-4"
            );
            newSequence.push(csEqual);

            if (
                currentI1 + thisSequenceLength <
                    (unknown.comparisonUnitArray1 as ComparisonUnitToken[])
                        .length ||
                currentI2 + thisSequenceLength <
                    (unknown.comparisonUnitArray2 as ComparisonUnitToken[])
                        .length
            ) {
                const csUnknown: CorrelatedSequence = new CorrelatedSequence(
                    Enumerable.from(unknown.comparisonUnitArray1)
                        .skip(currentI1 + thisSequenceLength)
                        .toArray(),
                    Enumerable.from(unknown.comparisonUnitArray2)
                        .skip(currentI2 + thisSequenceLength)
                        .toArray(),
                    CorrelationStatus.Unknown,
                    "doLcsAlgorithm-5"
                );
                newSequence.push(csUnknown);
            }
            return newSequence;
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    let currentLongestCommonSequenceLength = 0;
    let currentI1 = -1;
    let currentI2 = -1;
    for (
        let i1 = 0;
        i1 < cul1.length - currentLongestCommonSequenceLength;
        i1++
    ) {
        for (
            let i2 = 0;
            i2 < cul2.length - currentLongestCommonSequenceLength;
            i2++
        ) {
            let thisSequenceLength2 = 0;
            let thisI1 = i1;
            let thisI2 = i2;
            while (true) {
                if (cul1[thisI1].tokenString === cul2[thisI2].tokenString) {
                    thisI1++;
                    thisI2++;
                    thisSequenceLength2++;
                    if (thisI1 === cul1.length || thisI2 === cul2.length) {
                        if (
                            thisSequenceLength2 >
                            currentLongestCommonSequenceLength
                        ) {
                            currentLongestCommonSequenceLength = thisSequenceLength2;
                            currentI1 = i1;
                            currentI2 = i2;
                        }
                        break;
                    }
                    continue;
                } else {
                    if (
                        thisSequenceLength2 > currentLongestCommonSequenceLength
                    ) {
                        currentLongestCommonSequenceLength = thisSequenceLength2;
                        currentI1 = i1;
                        currentI2 = i2;
                    }
                    break;
                }
            }
        }
    }

    if (currentLongestCommonSequenceLength <= lcsSettings.detailThreshold) {
        currentLongestCommonSequenceLength = 0;
    }

    if (currentLongestCommonSequenceLength > 0) {
        const newSequence: CorrelatedSequence[] = [];
        if (currentI1 !== 0 || currentI2 !== 0) {
            const csUnknown: CorrelatedSequence = new CorrelatedSequence(
                Enumerable.from(unknown.comparisonUnitArray1)
                    .take(currentI1)
                    .toArray(),
                Enumerable.from(unknown.comparisonUnitArray2)
                    .take(currentI2)
                    .toArray(),
                CorrelationStatus.Unknown,
                "doLcsAlgorithm-6"
            );
            newSequence.push(csUnknown);
        }

        const csEqual: CorrelatedSequence = new CorrelatedSequence(
            Enumerable.from(unknown.comparisonUnitArray1)
                .skip(currentI1)
                .take(currentLongestCommonSequenceLength)
                .toArray(),
            Enumerable.from(unknown.comparisonUnitArray2)
                .skip(currentI2)
                .take(currentLongestCommonSequenceLength)
                .toArray(),
            CorrelationStatus.Equal,
            "doLcsAlgorithm-7"
        );
        newSequence.push(csEqual);

        if (
            currentI1 + currentLongestCommonSequenceLength <
                (unknown.comparisonUnitArray1 as ComparisonUnitToken[])
                    .length ||
            currentI2 + currentLongestCommonSequenceLength <
                (unknown.comparisonUnitArray2 as ComparisonUnitToken[]).length
        ) {
            const csUnknown: CorrelatedSequence = new CorrelatedSequence(
                Enumerable.from(unknown.comparisonUnitArray1)
                    .skip(currentI1 + currentLongestCommonSequenceLength)
                    .toArray(),
                Enumerable.from(unknown.comparisonUnitArray2)
                    .skip(currentI2 + currentLongestCommonSequenceLength)
                    .toArray(),
                CorrelationStatus.Unknown,
                "doLcsAlgorithm-8"
            );
            newSequence.push(csUnknown);
        }
        return newSequence;
    }

    // else currentLongestCommonSequenceLength === 0
    const newSequenceInsertedAndDeleted: CorrelatedSequence[] = [];

    const csDeleted: CorrelatedSequence = new CorrelatedSequence(
        unknown.comparisonUnitArray1,
        null,
        CorrelationStatus.Deleted,
        "doLcsAlgorithm-9"
    );
    newSequenceInsertedAndDeleted.push(csDeleted);

    const csInserted: CorrelatedSequence = new CorrelatedSequence(
        null,
        unknown.comparisonUnitArray2,
        CorrelationStatus.Inserted,
        "doLcsAlgorithm-10"
    );
    newSequenceInsertedAndDeleted.push(csInserted);

    return newSequenceInsertedAndDeleted;
}

function localSplice(
    csList: CorrelatedSequence[],
    index: number,
    newSequence: CorrelatedSequence[]
) {
    if (newSequence.length === 0) {
        csList.splice(index, 1);
    }
    if (newSequence.length === 1) {
        csList.splice(index, 1, newSequence[0]);
    }
    else if (newSequence.length === 2) {
        csList.splice(index, 1, newSequence[0], newSequence[1]);
    }
    else if (newSequence.length === 3) {
        csList.splice(index, 1, newSequence[0], newSequence[1], newSequence[2]);
    }
    else if (newSequence.length === 4) {
        csList.splice(
            index,
            1,
            newSequence[0],
            newSequence[1],
            newSequence[2],
            newSequence[3]
        );
    }
    else if (newSequence.length === 5) {
        csList.splice(
            index,
            1,
            newSequence[0],
            newSequence[1],
            newSequence[2],
            newSequence[3],
            newSequence[4]
        );
    }
}

function findCommonAtBeginningAndEnd(
    unknown: CorrelatedSequence,
    lcsSettings: LcsSettings
): CorrelatedSequence[] | null {
    const cua1 = unknown.comparisonUnitArray1 as ComparisonUnitToken[];
    const cua2 = unknown.comparisonUnitArray2 as ComparisonUnitToken[];
    const lengthToCompare = Math.min(cua1.length, cua2.length);

    // look for the special case of list items at the beginning of the line in DGML
    if (cua1.length > 0 && cua2.length > 4) {
        const leftTokenString = cua1[0].tokenString;
        let i = 0;
        for (i = 0; i < 4; ++i) {
            if (leftTokenString === cua2[i].tokenString) {
                break;
            }
        }
        if (i > 0 && i < 4) {
            let maybeListItem = "";
            for (let j = 0; j < i; ++j) {
                maybeListItem += cua2[j].tokenString;
            }

            if (isListItem(maybeListItem)) {
                const thisLengthToCompare = lengthToCompare - i;
                const countCommonAtBeginningSpecial = Enumerable.from(cua1)
                    .take(thisLengthToCompare)
                    .zip(
                        Enumerable.from(cua2).skip(i).take(thisLengthToCompare),
                        (
                            pu1: ComparisonUnitToken,
                            pu2: ComparisonUnitToken
                        ) => {
                            return {
                                pu1: pu1,
                                pu2: pu2,
                            };
                        }
                    )
                    .takeWhile(
                        (pair: any) =>
                            pair.pu1.tokenString === pair.pu2.tokenString
                    )
                    .count();

                if (
                    countCommonAtBeginningSpecial >= lcsSettings.detailThreshold
                ) {
                    const newSequence: CorrelatedSequence[] = [];

                    const csInserted: CorrelatedSequence = new CorrelatedSequence(
                        null,
                        Enumerable.from(unknown.comparisonUnitArray2)
                            .take(i)
                            .toArray(),
                        CorrelationStatus.Inserted,
                        "findCommonAtBeginningAndEnd-1"
                    );
                    newSequence.push(csInserted);

                    const csEqual: CorrelatedSequence = new CorrelatedSequence(
                        Enumerable.from(unknown.comparisonUnitArray1)
                            .take(countCommonAtBeginningSpecial)
                            .toArray(),
                        Enumerable.from(unknown.comparisonUnitArray2)
                            .skip(i)
                            .take(countCommonAtBeginningSpecial)
                            .toArray(),
                        CorrelationStatus.Equal,
                        "findCommonAtBeginningAndEnd-2"
                    );
                    newSequence.push(csEqual);

                    const csNewUnknown: CorrelatedSequence = new CorrelatedSequence(
                        Enumerable.from(unknown.comparisonUnitArray1)
                            .skip(countCommonAtBeginningSpecial)
                            .toArray(),
                        Enumerable.from(unknown.comparisonUnitArray2)
                            .skip(countCommonAtBeginningSpecial + i)
                            .toArray(),
                        CorrelationStatus.Unknown,
                        "findCommonAtBeginningAndEnd-3"
                    );
                    newSequence.push(csNewUnknown);

                    return newSequence;
                }
            }
        }
    }

    let countCommonAtBeginning = Enumerable.from(cua1)
        .take(lengthToCompare)
        .zip(
            Enumerable.from(cua2).take(lengthToCompare),
            (pu1: ComparisonUnitToken, pu2: ComparisonUnitToken) => {
                return {
                    pu1: pu1,
                    pu2: pu2,
                };
            }
        )
        .takeWhile(
            (pair: any) => pair.pu1.tokenString === pair.pu2.tokenString
        )
        .count();

    if (countCommonAtBeginning < lcsSettings.detailThreshold) {
        countCommonAtBeginning = 0;
    }

    if (countCommonAtBeginning !== 0) {
        const newSequence: CorrelatedSequence[] = [];

        const csEqual: CorrelatedSequence = new CorrelatedSequence(
            Enumerable.from(unknown.comparisonUnitArray1)
                .take(countCommonAtBeginning)
                .toArray(),
            Enumerable.from(unknown.comparisonUnitArray2)
                .take(countCommonAtBeginning)
                .toArray(),
            CorrelationStatus.Equal,
            "findCommonAtBeginningAndEnd-4"
        );
        newSequence.push(csEqual);

        const remainingLeft = cua1.length - countCommonAtBeginning;
        const remainingRight = cua2.length - countCommonAtBeginning;

        if (remainingLeft !== 0 || remainingRight !== 0) {
            const csNewUnknown: CorrelatedSequence = new CorrelatedSequence(
                Enumerable.from(unknown.comparisonUnitArray1)
                    .skip(countCommonAtBeginning)
                    .toArray(),
                Enumerable.from(unknown.comparisonUnitArray2)
                    .skip(countCommonAtBeginning)
                    .toArray(),
                CorrelationStatus.Unknown,
                "findCommonAtBeginningAndEnd-5"
            );
            newSequence.push(csNewUnknown);
        } else if (remainingLeft === 0 && remainingRight === 0) {
            // nothing to do
        }
        return newSequence;
    }

    // countCommonAtBeginning == 0
    let countCommonAtEnd = Enumerable.from(unknown.comparisonUnitArray1)
        .reverse()
        .take(lengthToCompare)
        .zip(
            Enumerable.from(unknown.comparisonUnitArray2)
                .reverse()
                .take(lengthToCompare),
            (pu1: ComparisonUnitToken, pu2: ComparisonUnitToken) => {
                return {
                    pu1: pu1,
                    pu2: pu2,
                };
            }
        )
        .takeWhile(
            (pair: any) => pair.pu1.tokenString === pair.pu2.tokenString
        )
        .count();

    if (countCommonAtEnd < lcsSettings.detailThreshold) {
        countCommonAtEnd = 0;
    }

    if (countCommonAtEnd !== 0) {
        const newSequence: CorrelatedSequence[] = [];

        const remainingLeft = cua1.length - countCommonAtEnd;
        const remainingRight = cua2.length - countCommonAtEnd;

        if (remainingLeft !== 0 || remainingRight !== 0) {
            const csNewUnknown: CorrelatedSequence = new CorrelatedSequence(
                Enumerable.from(unknown.comparisonUnitArray1)
                    .take(remainingLeft)
                    .toArray(),
                Enumerable.from(unknown.comparisonUnitArray2)
                    .take(remainingRight)
                    .toArray(),
                CorrelationStatus.Unknown,
                "findCommonAtBeginningAndEnd-6"
            );
            newSequence.push(csNewUnknown);
        }

        const csEqual: CorrelatedSequence = new CorrelatedSequence(
            Enumerable.from(unknown.comparisonUnitArray1)
                .skip(remainingLeft)
                .toArray(),
            Enumerable.from(unknown.comparisonUnitArray2)
                .skip(remainingRight)
                .toArray(),
            CorrelationStatus.Equal,
            "findCommonAtBeginningAndEnd-7"
        );
        newSequence.push(csEqual);

        return newSequence;
    }
    return null;
}

function isListItem(listItem: string): boolean {
    const regex = [/[0-9][.]/, /[A-Z][.]/];

    for (let i = 0; i < regex.length; ++i) {
        if (listItem.match(regex[i])) {
            return true;
        }
    }
    return false;
}
